import React, { useState, useEffect, useCallback } from 'react';
import { TASK_TYPES, BILLING_CODES } from '../../constants/timeTrackerConstants';
import { API_URL } from '../../constants/appConstants';
import { useCurrentTasks, TaskEditData } from '../../hooks/useCurrentTasks';
import { useIntranetData } from '../../hooks/useIntranetData';
import { useMondayTasks, useMondayTasksUpcoming, MondayTask } from '../../hooks/useMondayTasks';
import { useMondayTaskTimes } from '../../hooks/useMondayTaskTimes';
import { formatDuration, formatTime } from '../../utils/timeUtils';
import {
  getTaskTypeFields,
  getDescriptionPlaceholder,
  getWhyPlaceholder,
  getTaskTypeColor
} from '../../utils/taskTypeUtils';
import { TimeEntry, TimeTrackerProps } from '../../types/timeTracker';
import { IntranetAccount, ProjectForTaskCreation } from '../../types';
import TaskEditModal from './TaskEditModal';
import EstimateSubmitModal from './EstimateSubmitModal';
import PendingEstimatesPanel from './PendingEstimatesPanel';
import AfterHoursOverridePanel from './AfterHoursOverridePanel';
import { useTaskEstimates, TaskEstimate, SubmitEstimateData } from '../../hooks/useTaskEstimates';
import { useAfterHoursOverrides } from '../../hooks/useAfterHoursOverrides';
import './TimeTracker.css';

// Manager employee IDs (Josh) - Intranet IDs
// Note: Mauro (239) removed - now requires approval like other devs
const MANAGER_EMPLOYEE_IDS = ['239', '213', '16', '25', '155']; // 239=Mauro, 213=Josh (intranet), 16/25=BambooHR fallback

interface TaskCreationLookups {
  accounts: IntranetAccount[];
  projects: ProjectForTaskCreation[];
}

interface ConfirmedCompanySelection {
  companyString: string;
  projectId: string;
  project?: ProjectForTaskCreation;
  account?: IntranetAccount;
}

const isUsableAccount = (account?: Pick<IntranetAccount, 'id' | 'name'> | null): account is IntranetAccount =>
  Boolean(account?.id && account?.name && !/^\d+$/.test(account.name));

const formatCompanyString = (accountId: string, accountName: string): string => `${accountId} - ${accountName}`;

/** Intranet uses "-" for empty project/account cells; treat it as missing. */
const cleanTaskLabel = (value?: string | null): string => {
  const trimmed = String(value || '').trim();
  return !trimmed || trimmed === '-' || trimmed === '—' ? '' : trimmed;
};

const getTaskProjectLabel = (task: { projectName?: string; company?: string }): string =>
  cleanTaskLabel(task.projectName) || cleanTaskLabel(task.company);

const TimeTracker: React.FC<TimeTrackerProps> = ({ onTimerStart, onTimerStop }) => {
  // Data hooks
  const { tasks: currentTasks, completedTasks, toggleTask, closeTask, createTask, getTaskForEdit, updateTask, user: intranetUser } = useCurrentTasks();
  // Pass employee ID to useIntranetData for admin users (Mauro/Josh) who need HTML parsing
  const { accounts, loading, error, refetch: refetchIntranetData } = useIntranetData(intranetUser?.id);
  // Fetch Monday tasks for the logged-in user (auto-detected by email match)
  const { tasks: mondayTasks, getProjectSearchTerm, forceRefresh: refreshMondayTasks, loading: mondayLoading } = useMondayTasks();
  // Fetch upcoming Monday tasks (next 7 days)
  const { tasks: upcomingMondayTasks, loading: upcomingLoading, refetch: refreshUpcomingTasks } = useMondayTasksUpcoming();
  
  // Persistent time tracking for Monday tasks
  const { times: persistentTaskTimes, fetchTimes: fetchPersistentTimes, addTime: addPersistentTime, getTime: getPersistentTime } = useMondayTaskTimes(intranetUser?.id);
  
  // Local UI state
  const [togglingTaskId, setTogglingTaskId] = useState<string | null>(null);
  const [closingTaskId, setClosingTaskId] = useState<string | null>(null);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [startingMondayTaskId, setStartingMondayTaskId] = useState<string | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedBillingCode, setSelectedBillingCode] = useState('15'); // Default to Web Update
  const [description, setDescription] = useState('');
  const [whyDescription, setWhyDescription] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [todayEntries, setTodayEntries] = useState<TimeEntry[]>([]);
  const [showLog, setShowLog] = useState(true); // Always visible by default
  const [currentTime, setCurrentTime] = useState(Date.now()); // For live timer updates
  const [createError, setCreateError] = useState<string | null>(null);
  const [showActiveTasks, setShowActiveTasks] = useState(true);
  const [showTodaysLog, setShowTodaysLog] = useState(false); // Collapsed by default
  const [showUpcomingTasks, setShowUpcomingTasks] = useState(true); // Expanded by default for sprint view
  
  // Monday task type confirmation modal state
  const [pendingMondayTask, setPendingMondayTask] = useState<{
    task: MondayTask;
    account: { id: string; name: string };
    project?: { id: string; name: string } | null;
    taskType?: 'PROJECT' | 'FIX' | 'SUPPORT' | 'UPDATE' | 'INTERNAL';
  } | null>(null);

  // Monday task completion confirmation modal state
  const [pendingStopMondayTask, setPendingStopMondayTask] = useState<{
    intranetTask: typeof currentTasks[0];
    mondayTask: MondayTask;
    mondayTaskData: any;
    action: 'stop' | 'close';
    sessionTimeSeconds: number;
  } | null>(null);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);

  // Task edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [taskEditData, setTaskEditData] = useState<TaskEditData | null>(null);
  const [isLoadingTaskEdit, setIsLoadingTaskEdit] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);

  // Estimate workflow state
  const [isEstimateModalOpen, setIsEstimateModalOpen] = useState(false);
  const isManager = intranetUser?.id ? MANAGER_EMPLOYEE_IDS.includes(intranetUser.id) : false;
  
  // Estimate approval feature is always enabled
  const estimateFeatureEnabled = true;

  // After-hours override management
  const {
    overrides: afterHoursOverrides,
    loading: afterHoursLoading,
    hasOverrideToday,
    fetchOverrides: refreshAfterHoursOverrides,
    checkMyOverride,
    addOverride: addAfterHoursOverride,
    removeOverride: removeAfterHoursOverride
  } = useAfterHoursOverrides(intranetUser?.id, isManager);

  useEffect(function checkAfterHoursOverrideOnMount() {
    if (intranetUser?.id) {
      checkMyOverride(intranetUser.id);
    }
  }, [intranetUser?.id, checkMyOverride]);

  const WORK_HOURS_START = 6;
  const WORK_HOURS_END = 19;

  const isOutsideWorkingHours = (): boolean => {
    if (isManager) return false;
    if (hasOverrideToday) return false;
    const now = new Date();
    const hour = now.getHours();
    return hour < WORK_HOURS_START || hour >= WORK_HOURS_END;
  };

  const OUTSIDE_HOURS_MESSAGE =
    'Task creation is blocked outside of working hours (6 AM – 7 PM). ' +
    'Please contact a manager if you need to work outside of normal working hours.';
  
  // Task estimates hook
  const {
    myEstimates,
    pendingEstimates,
    loadingPending,
    submitEstimate,
    approveEstimate,
    rejectEstimate,
    startWork: startEstimateWork,
    deleteEstimate,
    rerequestApproval,
    resendNotification,
    refreshMyEstimates,
    refreshPendingEstimates,
    getRejectedEstimates
  } = useTaskEstimates(intranetUser?.id, isManager);

  // Derived state from task type
  const { showProjectDropdown, showAccountDropdown, showWhyField, showBillingCode } = 
    getTaskTypeFields(selectedTaskType);

  // All projects with account info (for PROJECT/FALLOUT task types and Monday matching)
  // Initialize from localStorage cache for instant display.
  // Cache key is versioned (v3) so payloads that lacked account mapping get discarded.
  const [allProjectsWithAccounts, setAllProjectsWithAccounts] = useState<ProjectForTaskCreation[]>(() => {
    try {
      localStorage.removeItem('edoss_cache_allProjects');
      localStorage.removeItem('edoss_cache_allProjects_v2');
      const cached = localStorage.getItem('edoss_cache_allProjects_v3');
      const parsed = cached ? JSON.parse(cached) : [];
      if (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        parsed.every((p) => p && p.id && p.accountId && p.accountName)
      ) {
        return parsed as ProjectForTaskCreation[];
      }
      return [];
    } catch { return []; }
  });
  const [loadingProjects, setLoadingProjects] = useState(() => {
    try {
      const cached = localStorage.getItem('edoss_cache_allProjects_v3');
      return !cached || JSON.parse(cached).length === 0;
    } catch { return true; }
  });
  const hadCachedProjectsOnMount = React.useRef(allProjectsWithAccounts.length > 0);

  // Reset fields when task type changes
  useEffect(() => {
    if (!showProjectDropdown) setSelectedProject('');
    if (!showAccountDropdown) setSelectedAccount('');
    if (!showWhyField) setWhyDescription('');
  }, [selectedTaskType, showProjectDropdown, showAccountDropdown, showWhyField]);

  // Auto-fill account when project is selected
  useEffect(() => {
    if (selectedProject && showProjectDropdown) {
      const project = allProjectsWithAccounts.find((p: any) => p.id === selectedProject);
      if (project) {
        setSelectedAccount(project.accountId);
      }
    }
  }, [selectedProject, showProjectDropdown, allProjectsWithAccounts]);

  // Ref so the mount effect below runs once; the id is read at call time
  // (Start/refresh calls happen after the user is known).
  const intranetUserIdRef = React.useRef<string | undefined>(intranetUser?.id);
  intranetUserIdRef.current = intranetUser?.id;

  const fetchFreshProjectsForTaskCreation = useCallback(async (): Promise<ProjectForTaskCreation[]> => {
    const employeeId = intranetUserIdRef.current;
    const queryParam = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : '';
    const response = await fetch(
      `${API_URL}/intranet/projects/all-with-accounts${queryParam}`,
      { credentials: 'include', cache: 'no-store' }
    );

    if (!response.ok) {
      throw new Error('Failed to refresh projects from the intranet');
    }

    const data = await response.json();
    const freshProjects = Array.isArray(data) ? data : [];

    if (freshProjects.length > 0) {
      setAllProjectsWithAccounts(freshProjects);
      try { localStorage.setItem('edoss_cache_allProjects_v3', JSON.stringify(freshProjects)); } catch {}
    }

    return freshProjects;
  }, []);

  // Fetch all projects with accounts on mount (needed for Monday task matching)
  // Always fetches fresh from intranet to stay in sync; uses localStorage/DB as instant cache
  useEffect(() => {
    const fetchAllProjects = async (isBackgroundRefresh = false) => {
      if (!isBackgroundRefresh) {
        setLoadingProjects(true);
      }
      
      try {
        // If we have no cached data, try local DB for instant display while intranet loads
        if (!isBackgroundRefresh && !hadCachedProjectsOnMount.current) {
          try {
            const dbResponse = await fetch(`${API_URL}/projects`, { credentials: 'include' });
            if (dbResponse.ok) {
              const dbData = await dbResponse.json();
              if (dbData && dbData.length > 0) {
                setAllProjectsWithAccounts(dbData);
              }
            }
          } catch {}
        }
        
        // Always fetch fresh from intranet (source of truth)
        await fetchFreshProjectsForTaskCreation();
      } catch (err) {
        console.error('Error fetching projects:', err);
      } finally {
        setLoadingProjects(false);
      }
    };
    
    const hasCachedData = hadCachedProjectsOnMount.current;
    fetchAllProjects(hasCachedData);
  }, [fetchFreshProjectsForTaskCreation]); // Run once on mount

  // Check if any intranet task is running
  const hasRunningTask = currentTasks.some(task => task.isRunning);

  // Track previously known in-progress estimate IDs to detect rejections (using ref to avoid infinite loop)
  const previousInProgressIdsRef = React.useRef<Set<number>>(new Set());
  
  // Auto-stop task if estimate is rejected
  useEffect(() => {
    // Get current in-progress estimates
    const currentInProgress = myEstimates.filter(e => e.status === 'in_progress');
    const currentIds = new Set(currentInProgress.map(e => e.id));
    
    // Find estimates that were in_progress but are now rejected
    const rejectedEstimates = myEstimates.filter(e => 
      e.status === 'rejected' && previousInProgressIdsRef.current.has(e.id)
    );
    
    if (rejectedEstimates.length > 0) {
      // Find and stop any running tasks that match rejected estimates
      rejectedEstimates.forEach(async (rejected) => {
        const matchingTask = currentTasks.find(task => 
          task.isRunning && 
          task.description.includes(rejected.task_description.substring(0, 30))
        );
        
        if (matchingTask) {
          console.log('[TimeTracker] Auto-stopping task due to rejected estimate:', rejected.task_description);
          await toggleTask(matchingTask);
          // Show alert to user
          alert(`⛔ Your estimate for "${rejected.task_description}" was rejected. The task has been stopped. Please check Slack for feedback.`);
        }
      });
    }
    
    // Update previous IDs for next comparison (using ref doesn't trigger re-render)
    previousInProgressIdsRef.current = currentIds;
  }, [myEstimates, currentTasks, toggleTask]);

  // Calculate elapsed time for a running task
  const getRunningTaskElapsed = useCallback((task: typeof currentTasks[0]): number => {
    if (!task.isRunning || !task.timeStart) return 0;
    const now = Math.floor(currentTime / 1000);
    return now - task.timeStart;
  }, [currentTime]);

  // Calculate total time today (including running intranet tasks and completed tasks)
  const getTotalTimeToday = useCallback((): number => {
    // Local entries + local tracking
    let total = todayEntries.reduce((sum, entry) => sum + entry.duration, 0);
    if (isTracking) total += elapsedTime;
    
    // Add time from active intranet tasks
    currentTasks.forEach(task => {
      // Add the stored time
      total += task.timeTotal;
      // Add live elapsed time if running
      if (task.isRunning) {
        total += getRunningTaskElapsed(task);
      }
    });
    
    // Add time from completed tasks (these are today's completed tasks)
    completedTasks.forEach(task => {
      total += task.timeTotal;
    });
    
    return total;
  }, [todayEntries, isTracking, elapsedTime, currentTasks, completedTasks, getRunningTaskElapsed]);

  // Global timer effect - ticks every second when any task is running
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (hasRunningTask || isTracking) {
      interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [hasRunningTask, isTracking]);

  // Timer effect for local tracking
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isTracking && startTime) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTracking, startTime]);

  // Fetch persistent time data when Monday tasks load
  useEffect(() => {
    const allMondayTaskIds = [...mondayTasks, ...upcomingMondayTasks].map(t => t.id);
    if (allMondayTaskIds.length > 0 && intranetUser?.id) {
      fetchPersistentTimes(allMondayTaskIds);
    }
  }, [mondayTasks, upcomingMondayTasks, intranetUser?.id, fetchPersistentTimes]);

  const getSelectedProjectName = (): string => {
    const project = allProjectsWithAccounts.find((p: any) => p.id === selectedProject);
    return project?.formattedName || project?.name || '';
  };

  const getSelectedAccountName = (): string => {
    const account = accounts.find(a => a.id === selectedAccount);
    return account?.name || selectedAccount;
  };

  /**
   * Parse an estimate string like "10-12" and return the status class based on time spent
   * @param estimate - Estimate string (e.g., "10-12", "10", "10+")
   * @param timeSpentSeconds - Time spent in seconds
   * @returns CSS class: '' | 'warning' | 'over'
   */
  const getEstimateStatusClass = (estimate: string | null, timeSpentSeconds: number): string => {
    if (!estimate) return '';
    
    // Parse estimate (e.g., "10-12" -> min: 10, max: 12)
    const rangeMatch = estimate.match(/^(\d+(?:\.\d+)?)\s*[-–—]\s*(\d+(?:\.\d+)?)/);
    const singleMatch = estimate.match(/^(\d+(?:\.\d+)?)/);
    
    let minHours = 0;
    let maxHours = 0;
    
    if (rangeMatch) {
      minHours = parseFloat(rangeMatch[1]);
      maxHours = parseFloat(rangeMatch[2]);
    } else if (singleMatch) {
      minHours = parseFloat(singleMatch[1]);
      maxHours = minHours;
    } else {
      return '';
    }
    
    const timeSpentHours = timeSpentSeconds / 3600;
    
    // Over max estimate
    if (maxHours > 0 && timeSpentHours >= maxHours) {
      return 'over';
    }
    
    // Approaching min estimate (80%+)
    if (minHours > 0 && timeSpentHours >= minHours * 0.8) {
      return 'warning';
    }
    
    return '';
  };

  /**
   * Get the total time spent on a Monday task
   * Combines: persistent DB time (historical) + current session time (live)
   * @param mondayTaskId - The Monday.com task ID
   * @returns Total time spent in seconds
   */
  const getTimeSpentOnMondayTask = useCallback((mondayTaskId: string): number => {
    // Start with persistent time from database (historical sessions)
    let totalSeconds = getPersistentTime(mondayTaskId);
    
    // Add current session time from intranet tasks (not yet saved to DB)
    const mondayRef = `[Monday #${mondayTaskId}]`;
    
    // Check current tasks (active and paused)
    currentTasks.forEach(task => {
      if (task.description.includes(mondayRef)) {
        totalSeconds += task.timeTotal;
        // Add live elapsed time if this task is currently running
        if (task.isRunning) {
          totalSeconds += getRunningTaskElapsed(task);
        }
      }
    });
    
    // Check completed tasks (today's completed, not yet synced to DB)
    completedTasks.forEach(task => {
      if (task.description.includes(mondayRef)) {
        totalSeconds += task.timeTotal;
      }
    });
    
    return totalSeconds;
  }, [currentTasks, completedTasks, getRunningTaskElapsed, getPersistentTime]);

  const canStart = (): boolean => {
    if (!selectedTaskType) return false;
    if (showProjectDropdown && !selectedProject) return false; // Project required for PROJECT/FALLOUT
    if (showAccountDropdown && !selectedAccount) return false; // Account required for other types
    if (!description.trim()) return false;
    if (showWhyField && !whyDescription.trim()) return false;
    return true;
  };

  const refreshTaskCreationLookups = useCallback(async (): Promise<TaskCreationLookups> => {
    const [intranetResult, projectsResult] = await Promise.allSettled([
      refetchIntranetData(intranetUser?.id, true),
      fetchFreshProjectsForTaskCreation()
    ]);

    const freshIntranetData =
      intranetResult.status === 'fulfilled' ? intranetResult.value : null;
    const freshProjects =
      projectsResult.status === 'fulfilled' ? projectsResult.value : null;

    if (!freshIntranetData && !freshProjects) {
      throw new Error('Unable to refresh accounts and projects from the intranet. Please try again.');
    }

    return {
      accounts: freshIntranetData?.accounts || [],
      projects: freshProjects || []
    };
  }, [fetchFreshProjectsForTaskCreation, intranetUser?.id, refetchIntranetData]);

  const findConfirmedProject = (
    projectId: string,
    lookups: TaskCreationLookups
  ): ProjectForTaskCreation | null => {
    const project = lookups.projects.find((candidate) => String(candidate.id) === String(projectId));
    if (!project) {
      return null;
    }

    if (project.accountId && project.accountName) {
      return project;
    }

    if (project.accountId) {
      const account = lookups.accounts.find((candidate) => String(candidate.id) === String(project.accountId));
      if (isUsableAccount(account)) {
        return { ...project, accountName: account.name };
      }
    }

    return null;
  };

  const findConfirmedAccount = (
    accountId: string,
    lookups: TaskCreationLookups
  ): IntranetAccount | null => {
    const account = lookups.accounts.find((candidate) => String(candidate.id) === String(accountId));
    if (isUsableAccount(account)) return account;

    const projectForAccount = lookups.projects.find((project) =>
      String(project.accountId) === String(accountId) &&
      Boolean(project.accountName)
    );

    if (!projectForAccount) return null;

    return {
      id: projectForAccount.accountId,
      name: projectForAccount.accountName,
      update_rate: null,
      enable_internal_task: null
    };
  };

  const confirmProjectCompanySelection = (
    projectId: string,
    lookups: TaskCreationLookups
  ): ConfirmedCompanySelection => {
    const project = findConfirmedProject(projectId, lookups);
    if (!project) {
      const listedProject = lookups.projects.find((candidate) => String(candidate.id) === String(projectId));
      if (listedProject) {
        throw new Error('This project is not linked to an account yet. Refresh and try again.');
      }
      throw new Error('Selected project is no longer available. Please refresh and select it again.');
    }

    return {
      companyString: formatCompanyString(project.accountId, project.accountName),
      projectId: project.id,
      project
    };
  };

  const confirmAccountCompanySelection = (
    accountId: string,
    lookups: TaskCreationLookups
  ): ConfirmedCompanySelection => {
    const account = findConfirmedAccount(accountId, lookups);
    if (!account) {
      throw new Error('Selected account is no longer available. Please refresh and select it again.');
    }

    return {
      companyString: formatCompanyString(account.id, account.name),
      projectId: '',
      account
    };
  };

  const handleStart = async () => {
    if (!canStart()) return;

    if (isOutsideWorkingHours()) {
      setCreateError(OUTSIDE_HOURS_MESSAGE);
      return;
    }
    
    setIsCreatingTask(true);
    setCreateError(null);
    
    try {
      const lookups = await refreshTaskCreationLookups();
      let selection: ConfirmedCompanySelection | null = null;
      
      if (showProjectDropdown && selectedProject) {
        selection = confirmProjectCompanySelection(selectedProject, lookups);
        setSelectedAccount(selection.project?.accountId || '');
      } else if (showAccountDropdown && selectedAccount) {
        selection = confirmAccountCompanySelection(selectedAccount, lookups);
      }

      if (!selection) {
        throw new Error('Please select a valid account or project before creating a task.');
      }
      
      // Create task on the intranet. daily_manager always posts billing_code
      // (defaults to "15") even for PROJECT — match that payload.
      const result = await createTask({
        company: selection.companyString,
        taskType: selectedTaskType,
        description: description,
        projectId: selection.projectId,
        descriptionWhy: whyDescription || '',
        billingCode: showBillingCode ? selectedBillingCode : '15',
        entry: 'START' // Start the task immediately
      });
      
      if (result?.success) {
        // Clear the form after successful creation
        setDescription('');
        setWhyDescription('');
        setSelectedProject('');
        // Keep task type and account selected for convenience
      } else {
        setCreateError(result?.message || 'Failed to create task');
      }
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create task');
    } finally {
      setIsCreatingTask(false);
    }
  };
  
  // Legacy local tracking start (kept for reference but not used)
  const handleLocalStart = () => {
    if (!canStart()) return;
    
    const now = new Date();
    setIsTracking(true);
    setStartTime(now);
    setElapsedTime(0);
    onTimerStart?.(now);
  };

  const handleStop = () => {
    if (startTime) {
      const newEntry: TimeEntry = {
        id: Date.now().toString(),
        accountId: selectedAccount,
        accountName: getSelectedAccountName(),
        projectId: selectedProject,
        projectName: showProjectDropdown ? getSelectedProjectName() : '',
        taskType: selectedTaskType,
        description: description,
        startTime: startTime,
        endTime: new Date(),
        duration: elapsedTime
      };
      setTodayEntries(prev => [newEntry, ...prev]);
    }
    setIsTracking(false);
    setStartTime(null);
    setElapsedTime(0);
    setDescription('');
    setWhyDescription('');
    onTimerStop?.();
  };

  const handleDeleteEntry = (id: string) => {
    setTodayEntries(prev => prev.filter(entry => entry.id !== id));
  };

  const handleToggleTask = async (task: typeof currentTasks[0]) => {
    const mondayMatch = task.description.match(/\[Monday #(\d+)\]/);
    let mondayTaskData = undefined;
    let mondayTask: MondayTask | undefined = undefined;

    if (mondayMatch) {
      const mondayId = mondayMatch[1];
      mondayTask = [...mondayTasks, ...upcomingMondayTasks].find(t => t.id === mondayId);
      if (mondayTask) {
        mondayTaskData = {
          id: mondayTask.id,
          name: mondayTask.name,
          boardName: mondayTask.boardName,
          boardId: mondayTask.boardId,
          estimate: mondayTask.estimate,
          mondayUrl: mondayTask.mondayUrl
        };
      }
    }

    if (task.isRunning && mondayTask && mondayTaskData) {
      const sessionTimeSeconds = getRunningTaskElapsed(task);
      setPendingStopMondayTask({
        intranetTask: task,
        mondayTask,
        mondayTaskData,
        action: 'stop',
        sessionTimeSeconds
      });
      return;
    }

    setTogglingTaskId(task.id);
    await toggleTask(task, mondayTaskData);
    setTogglingTaskId(null);
  };
  
  const addTimeCommentToMonday = async (itemId: string, boardId: string, totalSeconds: number, userName: string) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    try {
      await fetch(`${API_URL}/monday/items/${itemId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          boardId,
          comment: `${userName} worked on this task for a total of ${timeStr}`
        })
      });
    } catch (err) {
      console.error('Failed to add time comment to Monday:', err);
    }
  };

  const handleCloseMondayTask = async (markComplete: boolean) => {
    if (!pendingStopMondayTask) return;

    const { intranetTask, mondayTask, mondayTaskData, action, sessionTimeSeconds } = pendingStopMondayTask;

    if (action === 'stop' && !markComplete) {
      setTogglingTaskId(intranetTask.id);
      await toggleTask(intranetTask, mondayTaskData);
      setTogglingTaskId(null);

      if (sessionTimeSeconds > 0) {
        addPersistentTime(mondayTask.id, sessionTimeSeconds, {
          taskName: mondayTask.name,
          boardName: mondayTask.boardName
        });
      }

      setPendingStopMondayTask(null);
      return;
    }

    if (intranetTask.isRunning) {
      setTogglingTaskId(intranetTask.id);
      await toggleTask(intranetTask, mondayTaskData);
      setTogglingTaskId(null);
    }

    setClosingTaskId(intranetTask.id);
    await closeTask(intranetTask);

    const timeToAdd = action === 'stop' ? sessionTimeSeconds : intranetTask.timeTotal + (intranetTask.isRunning ? sessionTimeSeconds : 0);
    if (timeToAdd > 0) {
      addPersistentTime(mondayTask.id, timeToAdd, {
        taskName: mondayTask.name,
        boardName: mondayTask.boardName
      });
    }

    if (markComplete) {
      setIsMarkingComplete(true);

      const previousPersistent = getPersistentTime(mondayTask.id);
      const cumulativeSeconds = previousPersistent + timeToAdd;

      if (cumulativeSeconds > 0) {
        await addTimeCommentToMonday(
          mondayTask.id,
          mondayTask.boardId,
          cumulativeSeconds,
          intranetUser?.name || 'Developer'
        );
      }

      try {
        const response = await fetch(`${API_URL}/monday/items/${mondayTask.id}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ boardId: mondayTask.boardId })
        });

        if (response.ok) {
          refreshMondayTasks();
          refreshUpcomingTasks();
        } else {
          const error = await response.json();
          console.error('Failed to mark Monday task complete:', error);
          setCreateError(`Task closed but couldn't mark complete on Monday: ${error.error || 'Unknown error'}`);
        }
      } catch (err: any) {
        console.error('Error marking Monday task complete:', err);
        setCreateError(`Task closed but couldn't mark complete on Monday: ${err.message}`);
      } finally {
        setIsMarkingComplete(false);
      }
    }

    setClosingTaskId(null);
    setPendingStopMondayTask(null);
  };

  const handleCloseTask = async (task: typeof currentTasks[0]) => {
    // Check if this is a Monday task to track time persistently
    const mondayMatch = task.description.match(/\[Monday #(\d+)\]/);
    let mondayId: string | null = null;
    
    if (mondayMatch) {
      mondayId = mondayMatch[1];
      const mondayTask = [...mondayTasks, ...upcomingMondayTasks].find(t => t.id === mondayId);
      
      // If this is a Monday task, show the completion confirmation modal
      if (mondayTask) {
        const mondayTaskData = {
          id: mondayTask.id,
          name: mondayTask.name,
          boardName: mondayTask.boardName,
          boardId: mondayTask.boardId,
          estimate: mondayTask.estimate,
          mondayUrl: mondayTask.mondayUrl
        };
        
        const sessionTimeSeconds = task.isRunning ? getRunningTaskElapsed(task) : 0;
        setPendingStopMondayTask({
          intranetTask: task,
          mondayTask: mondayTask,
          mondayTaskData: mondayTaskData,
          action: 'close',
          sessionTimeSeconds
        });
        return;
      }
    }
    
    // Non-Monday task - close normally
    // If task is running, stop it first
    if (task.isRunning) {
      setTogglingTaskId(task.id);
      await toggleTask(task, undefined);
      setTogglingTaskId(null);
    }
    
    setClosingTaskId(task.id);
    
    // Close the task - the hook will refetch and update completedTasks
    await closeTask(task);
    
    setClosingTaskId(null);
  };

  /**
   * Open the edit modal for a task
   */
  const handleOpenEditModal = async (taskId: string) => {
    setIsEditModalOpen(true);
    setIsLoadingTaskEdit(true);
    setTaskEditData(null);
    
    try {
      const data = await getTaskForEdit(taskId);
      setTaskEditData(data);
    } catch (err) {
      console.error('Failed to load task for editing:', err);
    } finally {
      setIsLoadingTaskEdit(false);
    }
  };

  /**
   * Close the edit modal
   */
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setTaskEditData(null);
  };

  /**
   * Save the edited task
   */
  const handleSaveTask = async (taskData: TaskEditData) => {
    setIsSavingTask(true);
    
    try {
      const result = await updateTask(taskData);
      if (result?.success) {
        handleCloseEditModal();
      } else {
        throw new Error(result?.message || 'Failed to update task');
      }
    } finally {
      setIsSavingTask(false);
    }
  };

  /**
   * Open estimate submit modal instead of creating task directly
   */
  const handleOpenEstimateModal = async () => {
    if (!canStart()) return;

    if (isOutsideWorkingHours()) {
      setCreateError(OUTSIDE_HOURS_MESSAGE);
      return;
    }

    setIsCreatingTask(true);
    setCreateError(null);

    try {
      const lookups = await refreshTaskCreationLookups();
      if (showProjectDropdown && selectedProject) {
        const selection = confirmProjectCompanySelection(selectedProject, lookups);
        setSelectedAccount(selection.project?.accountId || '');
      } else if (showAccountDropdown && selectedAccount) {
        confirmAccountCompanySelection(selectedAccount, lookups);
      }

      setIsEstimateModalOpen(true);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to confirm the selected account');
    } finally {
      setIsCreatingTask(false);
    }
  };

  /**
   * Submit estimate for approval and immediately start working on the task
   * (Developers can work up to 1 hour before approval is required)
   */
  const handleSubmitEstimate = async (data: SubmitEstimateData) => {
    if (isOutsideWorkingHours()) {
      return { success: false, message: OUTSIDE_HOURS_MESSAGE };
    }

    setIsCreatingTask(true);
    setCreateError(null);
    
    try {
      const lookups = await refreshTaskCreationLookups();
      const taskTypeFields = getTaskTypeFields(data.taskType);
      let selection: ConfirmedCompanySelection | null = null;

      if (taskTypeFields.showProjectDropdown && data.projectId) {
        selection = confirmProjectCompanySelection(data.projectId, lookups);
      } else if (taskTypeFields.showAccountDropdown && data.accountId) {
        selection = confirmAccountCompanySelection(data.accountId, lookups);
      }

      if (!selection) {
        return { success: false, message: 'Please select a valid account or project before submitting an estimate.' };
      }

      const confirmedEstimateData: SubmitEstimateData = {
        ...data,
        projectId: selection.projectId || undefined,
        projectName: selection.project?.name || data.projectName,
        accountId: selection.account?.id || selection.project?.accountId || data.accountId,
        accountName: selection.account?.name || selection.project?.accountName || data.accountName
      };

      // 1. Submit the estimate for approval
      const result = await submitEstimate(confirmedEstimateData);
      if (!result.success) {
        return result;
      }
      
      // 3. Create the actual task on the intranet
      const createResult = await createTask({
        company: selection.companyString,
        taskType: confirmedEstimateData.taskType,
        description: confirmedEstimateData.taskDescription,
        projectId: selection.projectId,
        descriptionWhy: '',
        billingCode: '15',
        entry: 'START'
      });
      
      if (createResult?.success && result.estimate) {
        // 4. Mark the estimate as in_progress so the 1-hour limit starts
        await startEstimateWork(result.estimate.id);
        await refreshMyEstimates();
        
        // Clear the form after successful submission
        setDescription('');
        setWhyDescription('');
        setSelectedProject('');
        
        return { 
          success: true, 
          message: 'Task started! You have up to 1 hour to work before approval is required.' 
        };
      } else {
        setCreateError(createResult?.message || 'Failed to create task on intranet');
        return { 
          success: false, 
          message: createResult?.message || 'Estimate submitted but failed to start task' 
        };
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to submit estimate';
      setCreateError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsCreatingTask(false);
    }
  };

  /**
   * Approve an estimate (manager action)
   */
  const handleApproveEstimate = async (id: number, notes?: string) => {
    if (!intranetUser?.id) return { success: false, message: 'Not logged in' };
    return approveEstimate(id, intranetUser.id, intranetUser.name || 'Manager', notes);
  };

  /**
   * Reject an estimate (manager action)
   */
  const handleRejectEstimate = async (id: number, reason: string) => {
    if (!intranetUser?.id) return { success: false, message: 'Not logged in' };
    return rejectEstimate(id, intranetUser.id, intranetUser.name || 'Manager', reason);
  };

  /**
   * Start working on an approved estimate
   */
  const handleStartApprovedEstimate = async (estimate: TaskEstimate) => {
    if (!intranetUser?.id) return;

    if (isOutsideWorkingHours()) {
      setCreateError(OUTSIDE_HOURS_MESSAGE);
      return;
    }
    
    setIsCreatingTask(true);
    setCreateError(null);
    
    try {
      const lookups = await refreshTaskCreationLookups();
      const taskTypeFields = getTaskTypeFields(estimate.task_type);
      let selection: ConfirmedCompanySelection | null = null;

      if (taskTypeFields.showProjectDropdown && estimate.project_id) {
        selection = confirmProjectCompanySelection(estimate.project_id, lookups);
      } else if (estimate.account_id) {
        selection = confirmAccountCompanySelection(estimate.account_id, lookups);
      }

      if (!selection) {
        throw new Error('Unable to confirm the estimate account or project. Please refresh and try again.');
      }
      
      // Create task on the intranet
      const result = await createTask({
        company: selection.companyString,
        taskType: estimate.task_type,
        description: estimate.task_description,
        projectId: selection.projectId,
        descriptionWhy: '',
        billingCode: '15',
        entry: 'START'
      });
      
      if (result?.success) {
        // Mark estimate as in progress (intranet doesn't return task ID)
        await startEstimateWork(estimate.id);
        await refreshMyEstimates();
      } else {
        setCreateError(result?.message || 'Failed to create task');
      }
    } catch (err: any) {
      setCreateError(err.message || 'Failed to start task');
    } finally {
      setIsCreatingTask(false);
    }
  };

  // Get approved estimates - only status 'approved' AND work hasn't started yet
  // If work_started_at is set, task is already running so don't show here
  const approvedEstimates = myEstimates.filter(e => 
    e.status === 'approved' && !e.work_started_at
  );
  
  // Get pending estimates (waiting for approval) - only show 'pending' status
  // Once work starts (in_progress), the task is in Active Tasks so no need to show here
  const myPendingEstimates = myEstimates.filter(e => e.status === 'pending');
  
  // Get rejected estimates (need revision)
  const myRejectedEstimates = myEstimates.filter(e => e.status === 'rejected');
  
  // State for re-request modal
  const [rerequestModalOpen, setRerequestModalOpen] = useState(false);
  const [rerequestEstimate, setRerequestEstimate] = useState<TaskEstimate | null>(null);
  const [rerequestHours, setRerequestHours] = useState('');
  const [rerequestResponse, setRerequestResponse] = useState('');
  const [isRerequesting, setIsRerequesting] = useState(false);
  
  // State for resending notification
  const [resendingNotificationId, setResendingNotificationId] = useState<number | null>(null);
  
  // State for dismissing pending estimates
  const [dismissingEstimateId, setDismissingEstimateId] = useState<number | null>(null);

  /**
   * Open the re-request modal
   */
  const openRerequestModal = (estimate: TaskEstimate) => {
    setRerequestEstimate(estimate);
    setRerequestHours(String(estimate.estimate_hours));
    setRerequestResponse('');
    setRerequestModalOpen(true);
  };

  /**
   * Close the re-request modal
   */
  const closeRerequestModal = () => {
    setRerequestModalOpen(false);
    setRerequestEstimate(null);
    setRerequestHours('');
    setRerequestResponse('');
  };

  /**
   * Handle resending Slack notification for a pending estimate
   */
  const handleResendNotification = async (estimate: TaskEstimate) => {
    if (!intranetUser?.id || !intranetUser?.name) return;
    
    setResendingNotificationId(estimate.id);
    
    try {
      const result = await resendNotification(
        estimate.id,
        intranetUser.id,
        intranetUser.name
      );
      
      if (!result.success) {
        setCreateError(result.message || 'Failed to resend notification');
      }
    } catch (err: any) {
      setCreateError(err.message || 'Failed to resend notification');
    } finally {
      setResendingNotificationId(null);
    }
  };

  /**
   * Handle dismissing/deleting an unstarted estimate (cleanup for old estimates)
   */
  const handleDismissEstimate = async (estimate: TaskEstimate) => {
    if (!intranetUser?.id) return;

    if (estimate.status === 'approved') {
      const confirmed = window.confirm('Remove this approved estimate from your dashboard? This cannot be undone.');
      if (!confirmed) return;
    }
    
    setDismissingEstimateId(estimate.id);
    
    try {
      const result = await deleteEstimate(estimate.id, intranetUser.id);
      
      if (!result.success) {
        setCreateError(result.message || 'Failed to dismiss estimate');
      }
    } catch (err: any) {
      setCreateError(err.message || 'Failed to dismiss estimate');
    } finally {
      setDismissingEstimateId(null);
    }
  };

  /**
   * Handle re-requesting approval for a rejected estimate with updated hours/response
   */
  const handleRerequestApproval = async () => {
    if (!intranetUser?.id || !intranetUser?.name || !rerequestEstimate) return;
    
    setIsRerequesting(true);
    
    try {
      // Build notes with response if provided
      let notes = '';
      if (rerequestResponse.trim()) {
        notes = `Response to feedback: ${rerequestResponse.trim()}`;
      }
      
      const result = await rerequestApproval(
        rerequestEstimate.id,
        intranetUser.id,
        intranetUser.name,
        parseFloat(rerequestHours) || rerequestEstimate.estimate_hours,
        notes || undefined
      );
      
      if (result.success) {
        closeRerequestModal();
      } else {
        setCreateError(result.message || 'Failed to re-request approval');
      }
    } catch (err: any) {
      setCreateError(err.message || 'Failed to re-request approval');
    } finally {
      setIsRerequesting(false);
    }
  };

  /**
   * Start a Monday.com task by creating an intranet task and starting the timer
   * Auto-matches the Monday board to an intranet project, or falls back to account with task type selection
   * If an intranet task already exists for this Monday ID, it will resume that task instead
   */
  const handleStartMondayTask = async (mondayTask: MondayTask) => {
    const existingTask = currentTasks.find(t => t.description.includes(`[Monday #${mondayTask.id}]`));
    
    if (existingTask) {
      setStartingMondayTaskId(mondayTask.id);
      try {
        await handleToggleTask(existingTask);
      } finally {
        setStartingMondayTaskId(null);
      }
      return;
    }

    if (isOutsideWorkingHours()) {
      setCreateError(OUTSIDE_HOURS_MESSAGE);
      return;
    }
    
    setPendingMondayTask({
      task: mondayTask,
      account: { id: '', name: '' },
      project: null,
      taskType: 'PROJECT'
    });
  };

  /**
   * Confirm and start a Monday task from modal selections
   */
  const handleConfirmMondayTaskType = async () => {
    if (!pendingMondayTask) return;

    if (isOutsideWorkingHours()) {
      setCreateError(OUTSIDE_HOURS_MESSAGE);
      setPendingMondayTask(null);
      return;
    }
    
    const { task: mondayTask, taskType = 'PROJECT', account, project } = pendingMondayTask;
    setStartingMondayTaskId(mondayTask.id);
    setPendingMondayTask(null);
    
    try {
      const lookups = await refreshTaskCreationLookups();
      let selection: ConfirmedCompanySelection | null = null;
      let billingCode = '';
      const isProjectType = taskType === 'PROJECT' || taskType === 'FIX';
      
      if (isProjectType && project?.id) {
        selection = confirmProjectCompanySelection(project.id, lookups);
      } else if (account?.id) {
        selection = confirmAccountCompanySelection(account.id, lookups);
      } else {
        throw new Error(isProjectType ? 'Please select a project' : 'Please select an account');
      }

      if (!selection) {
        throw new Error('Please select a valid account or project');
      }
      
      if (taskType === 'UPDATE') {
        billingCode = '15'; // Default billing code for updates
      }
      
      // Create the task description with Monday reference
      const taskDescription = `[Monday #${mondayTask.id}] ${mondayTask.name}`;
      
      // Create task on the intranet and start it immediately
      const result = await createTask({
        company: selection.companyString,
        taskType: taskType,
        description: taskDescription,
        projectId: selection.projectId,
        descriptionWhy: '',
        billingCode: billingCode,
        entry: 'START' // Start the task immediately
      });
      
      if (!result?.success) {
        setCreateError(result?.message || 'Failed to create task from Monday item');
      }
    } catch (err: any) {
      setCreateError(err.message || 'Failed to start Monday task');
    } finally {
      setStartingMondayTaskId(null);
    }
  };

  if (loading) {
    return (
      <div className="time-tracker-loading">
        <div className="loading-spinner"></div>
        <span>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="time-tracker-error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{error}</span>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="time-tracker time-tracker-wide">
      {/* Monday Task Start Modal */}
      {pendingMondayTask && (
        <div className="monday-task-modal-overlay" onClick={() => setPendingMondayTask(null)}>
          <div className="monday-start-modal" onClick={(e) => e.stopPropagation()}>
            <button className="monday-start-modal-close" onClick={() => setPendingMondayTask(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="monday-start-modal-task">
              <span className="monday-start-modal-badge">Monday Task</span>
              <h3 className="monday-start-modal-title">{pendingMondayTask.task.name}</h3>
            </div>

            <div className="monday-start-modal-section">
              <span className="monday-start-modal-label">Task Type</span>
              <div className="monday-start-type-grid">
                {(['PROJECT', 'FIX', 'SUPPORT', 'UPDATE', 'INTERNAL'] as const).map(type => (
                  <button
                    key={type}
                    className={`monday-start-type-btn monday-start-type-${type.toLowerCase()} ${pendingMondayTask.taskType === type ? 'active' : ''}`}
                    onClick={() => setPendingMondayTask({
                      ...pendingMondayTask,
                      taskType: type,
                      ...(type !== 'PROJECT' && type !== 'FIX' ? { project: null } : {})
                    })}
                  >
                    {type.charAt(0) + type.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>

            {(pendingMondayTask.taskType === 'PROJECT' || pendingMondayTask.taskType === 'FIX') ? (
              <div className="monday-start-modal-section">
                <span className="monday-start-modal-label">Project</span>
                <select
                  className="monday-start-select"
                  value={pendingMondayTask.project?.id || ''}
                  onChange={(e) => {
                    const selected = allProjectsWithAccounts.find((p: any) => p.id === e.target.value);
                    setPendingMondayTask({
                      ...pendingMondayTask,
                      project: selected ? { id: selected.id, name: selected.name || selected.formattedName || '' } : null,
                      account: selected
                        ? { id: selected.accountId, name: selected.accountName }
                        : { id: '', name: '' }
                    });
                  }}
                >
                  <option value="">Choose a project...</option>
                  {allProjectsWithAccounts.map((project: any) => (
                    <option key={`proj-${project.id}`} value={project.id}>
                      {project.name || project.formattedName}
                    </option>
                  ))}
                </select>
                {pendingMondayTask.account.name && (
                  <span className="monday-start-linked">
                    Account: <strong>{pendingMondayTask.account.name}</strong>
                  </span>
                )}
              </div>
            ) : (
              <div className="monday-start-modal-section">
                <span className="monday-start-modal-label">Account</span>
                <select
                  className="monday-start-select"
                  value={pendingMondayTask.account.id || ''}
                  onChange={(e) => {
                    const selected = accounts.find((a: any) => a.id === e.target.value);
                    const linkedProject = allProjectsWithAccounts.find((p: any) => p.accountId === e.target.value);
                    setPendingMondayTask({
                      ...pendingMondayTask,
                      account: selected ? { id: selected.id, name: selected.name } : { id: '', name: '' },
                      project: linkedProject
                        ? { id: linkedProject.id, name: linkedProject.name || linkedProject.formattedName || '' }
                        : null
                    });
                  }}
                >
                  <option value="">Choose an account...</option>
                  {accounts.map((account: any) => (
                    <option key={`acc-${account.id}`} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
                {pendingMondayTask.project?.name && (
                  <span className="monday-start-linked">
                    Linked Project: <strong>{pendingMondayTask.project.name}</strong>
                  </span>
                )}
              </div>
            )}

            <button
              className="monday-start-confirm-btn"
              onClick={handleConfirmMondayTaskType}
              disabled={
                (pendingMondayTask.taskType === 'PROJECT' || pendingMondayTask.taskType === 'FIX')
                  ? !pendingMondayTask.project?.id
                  : !pendingMondayTask.account.id
              }
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Start Task
            </button>
          </div>
        </div>
      )}

      {/* Monday Task Completion Confirmation Modal */}
      {pendingStopMondayTask && (() => {
        const { mondayTask, action, sessionTimeSeconds } = pendingStopMondayTask;
        const totalTracked = getTimeSpentOnMondayTask(mondayTask.id);
        const hasEstimate = !!mondayTask.estimate;
        const estimateHours = hasEstimate ? parseFloat(mondayTask.estimate!) : 0;
        const estimateSeconds = estimateHours * 3600;
        const progressPct = hasEstimate && estimateSeconds > 0
          ? Math.min(100, Math.round((totalTracked / estimateSeconds) * 100))
          : 0;

        return (
          <div className="monday-task-modal-overlay" onClick={() => setPendingStopMondayTask(null)}>
            <div className="monday-done-modal" onClick={(e) => e.stopPropagation()}>
              <button
                className="monday-done-modal-close"
                onClick={() => setPendingStopMondayTask(null)}
                disabled={isMarkingComplete}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>

              <div className="monday-done-modal-meta">
                <span className="monday-done-board-name">{mondayTask.boardName}</span>
                {mondayTask.groupName && (
                  <span className="monday-done-group-name">{mondayTask.groupName}</span>
                )}
              </div>

              <p className="monday-done-modal-task">{mondayTask.name}</p>

              <div className="monday-done-modal-stats">
                <div className="monday-done-stat">
                  <span className="monday-done-stat-label">This session</span>
                  <span className="monday-done-stat-value">{formatDuration(sessionTimeSeconds)}</span>
                </div>
                <div className="monday-done-stat">
                  <span className="monday-done-stat-label">Total tracked</span>
                  <span className="monday-done-stat-value monday-done-stat-total">{formatDuration(totalTracked)}</span>
                </div>
                {hasEstimate && (
                  <div className="monday-done-stat">
                    <span className="monday-done-stat-label">Estimate</span>
                    <span className="monday-done-stat-value">{mondayTask.estimate}h</span>
                  </div>
                )}
              </div>

              {hasEstimate && (
                <div className="monday-done-progress-track">
                  <div
                    className={`monday-done-progress-fill ${progressPct >= 100 ? 'over' : progressPct >= 80 ? 'warn' : ''}`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              )}

              <div className="monday-done-modal-status">
                <span className="monday-done-status-pill">{mondayTask.status || 'Working on it'}</span>
                <svg className="monday-done-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 5l4 3-4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="monday-done-status-pill monday-done-status-done">Done</span>
              </div>

              <p className="monday-done-modal-hint">
                {action === 'stop'
                  ? 'Mark this task as Done on Monday? If not, time will keep being tracked on eDoss.'
                  : 'Is this task finished? This will update the status on Monday.com.'}
              </p>

              <div className="monday-done-modal-actions">
                <button
                  className="monday-done-btn monday-done-btn-confirm"
                  onClick={() => handleCloseMondayTask(true)}
                  disabled={isMarkingComplete}
                >
                  {isMarkingComplete ? 'Updating Monday...' : 'Mark as Done'}
                </button>
                <button
                  className="monday-done-btn monday-done-btn-secondary"
                  onClick={() => handleCloseMondayTask(false)}
                  disabled={isMarkingComplete}
                >
                  {action === 'stop' ? 'Just Pause' : 'Keep Open'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Task Edit Modal */}
      <TaskEditModal
        task={taskEditData}
        isOpen={isEditModalOpen}
        isLoading={isLoadingTaskEdit}
        isSaving={isSavingTask}
        accounts={accounts}
        projects={allProjectsWithAccounts.map(p => ({ id: p.id, name: p.name || p.formattedName || '', accountId: p.accountId }))}
        onClose={handleCloseEditModal}
        onSave={handleSaveTask}
      />

      {/* Estimate Submit Modal */}
      <EstimateSubmitModal
        isOpen={isEstimateModalOpen}
        onClose={() => setIsEstimateModalOpen(false)}
        onSubmit={handleSubmitEstimate}
        taskDescription={description}
        taskType={selectedTaskType}
        projectId={selectedProject || undefined}
        projectName={showProjectDropdown ? getSelectedProjectName() : undefined}
        accountId={selectedAccount || undefined}
        accountName={showAccountDropdown ? getSelectedAccountName() : undefined}
        employeeId={intranetUser?.id || ''}
        employeeName={intranetUser?.name || ''}
      />

      {/* Re-request Approval Modal */}
      {rerequestModalOpen && rerequestEstimate && (
        <div className="modal-overlay" onClick={closeRerequestModal}>
          <div className="rerequest-modal" onClick={e => e.stopPropagation()}>
            <div className="rerequest-modal-header">
              <h3>Revise & Re-request Approval</h3>
              <button className="modal-close-btn" onClick={closeRerequestModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="rerequest-modal-body">
              {/* Task info */}
              <div className="rerequest-task-info">
                <div className="rerequest-task-name">{rerequestEstimate.task_description}</div>
                <div className="rerequest-task-meta">
                  <span>{rerequestEstimate.task_type}</span>
                  {rerequestEstimate.project_name && <span>{rerequestEstimate.project_name}</span>}
                </div>
              </div>

              {/* Original rejection feedback */}
              {rerequestEstimate.approval_notes && (
                <div className="rerequest-feedback-box">
                  <div className="feedback-label">Manager Feedback:</div>
                  <div className="feedback-text">{rerequestEstimate.approval_notes}</div>
                </div>
              )}

              {/* Updated estimate hours */}
              <div className="rerequest-field">
                <label htmlFor="rerequest-hours">Updated Estimate (hours)</label>
                <input
                  id="rerequest-hours"
                  type="number"
                  step="0.25"
                  min="0.25"
                  value={rerequestHours}
                  onChange={e => setRerequestHours(e.target.value)}
                  className="rerequest-input"
                />
                <div className="field-hint">
                  Original: {rerequestEstimate.estimate_hours}h
                </div>
              </div>

              {/* Response to feedback */}
              <div className="rerequest-field">
                <label htmlFor="rerequest-response">Response to Feedback (optional)</label>
                <textarea
                  id="rerequest-response"
                  value={rerequestResponse}
                  onChange={e => setRerequestResponse(e.target.value)}
                  placeholder="Explain any changes or respond to the manager's feedback..."
                  className="rerequest-textarea"
                  rows={3}
                />
              </div>
            </div>

            <div className="rerequest-modal-footer">
              <button className="rerequest-cancel-btn" onClick={closeRerequestModal}>
                Cancel
              </button>
              <button 
                className="rerequest-submit-btn" 
                onClick={handleRerequestApproval}
                disabled={isRerequesting || !rerequestHours || parseFloat(rerequestHours) <= 0}
              >
                {isRerequesting ? (
                  <div className="btn-spinner"></div>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="23 4 23 10 17 10"></polyline>
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                    </svg>
                    Re-submit for Approval
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estimate approval is always required — toggle removed */}

      {/* Task Type Row - Always First */}
      <div className="tracker-main-row">
        <div className="tracker-inputs-wide">
          <div className="input-group task-type-group">
            <label className="input-label">Task Type</label>
            <select
              className="tracker-select task-type-select"
              value={selectedTaskType}
              onChange={(e) => setSelectedTaskType(e.target.value)}
              disabled={isTracking}
            >
              <option value="">- Task Type -</option>
              <optgroup label="Project">
                {TASK_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Project Dropdown - shown for PROJECT, FALLOUT */}
          {showProjectDropdown && (
            <div className="input-group project-group">
              <label className="input-label">Project</label>
              <select
                className="tracker-select"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                disabled={isTracking || isCreatingTask || loadingProjects}
              >
                <option value="">
                  {loadingProjects ? 'Loading projects...' : '-- SELECT PROJECT --'}
                </option>
                {allProjectsWithAccounts.map((project: any) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Account Dropdown - shown when project is selected (auto-filled, read-only) OR for non-project task types */}
          {(showProjectDropdown && selectedProject) && (
            <div className="input-group account-group">
              <label className="input-label">Account</label>
              <input
                type="text"
                className="tracker-input"
                value={(() => {
                  const project = allProjectsWithAccounts.find((p: any) => p.id === selectedProject);
                  return project?.accountName || '';
                })()}
                disabled
                readOnly
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', cursor: 'not-allowed' }}
              />
            </div>
          )}

          {/* Account Dropdown - shown for INTERNAL, UPDATE, FIX, etc. (no project needed) */}
          {showAccountDropdown && !showProjectDropdown && (
            <div className="input-group account-group">
              <label className="input-label">Account</label>
              <select
                className="tracker-select"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                disabled={isTracking || isCreatingTask}
              >
                <option value="">-- SELECT ACCOUNT --</option>
                {accounts
                  .filter(account => !/^\d+$/.test(account.name)) // Filter out numeric-only names
                  .map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Billing Code - shown only for UPDATE */}
          {showBillingCode && (
            <div className="input-group billing-group">
              <label className="input-label">Billing Code</label>
              <select
                className="tracker-select"
                value={selectedBillingCode}
                onChange={(e) => setSelectedBillingCode(e.target.value)}
                disabled={isTracking}
              >
                {BILLING_CODES.map(code => (
                  <option key={code.value} value={code.value}>
                    {code.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Control Buttons */}
        <div className="tracker-controls-wide">
          <button 
            className="tracker-btn estimate-btn"
            onClick={(isManager || !estimateFeatureEnabled) ? handleStart : handleOpenEstimateModal}
            disabled={!canStart() || isCreatingTask}
            title={(isManager || !estimateFeatureEnabled) ? "Start task directly" : "Submit estimate for approval before starting"}
          >
            {isCreatingTask ? (
              <>
                <div className="btn-spinner"></div>
                Creating...
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                {(isManager || !estimateFeatureEnabled) ? 'Start Task' : 'Submit Task'}
              </>
            )}
          </button>
        </div>

        {/* Today's Total & Toggle */}
        <div className="tracker-summary">
          <div className="summary-total">
            <span className="summary-label">Task Times</span>
            <span className="summary-time">{formatTime(getTotalTimeToday())}</span>
          </div>
          
          <button 
            className={`log-toggle-btn ${showLog ? 'active' : ''}`}
            onClick={() => setShowLog(!showLog)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6"></line>
              <line x1="4" y1="12" x2="20" y2="12"></line>
              <line x1="4" y1="18" x2="20" y2="18"></line>
            </svg>
            {todayEntries.length > 0 && (
              <span className="log-count">{todayEntries.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Why Field - shown for FALLOUT, FIX */}
      {showWhyField && (
        <div className="tracker-why-row" style={{ background: getTaskTypeColor(selectedTaskType) }}>
          <div className="input-group why-group">
            <label className="input-label why-label">Why?</label>
            <input
              type="text"
              className="tracker-input why-input"
              placeholder={getWhyPlaceholder(selectedTaskType)}
              value={whyDescription}
              onChange={(e) => setWhyDescription(e.target.value)}
              disabled={isTracking}
            />
          </div>
        </div>
      )}

      {/* Description Field */}
      <div className="tracker-description-row" style={{ background: getTaskTypeColor(selectedTaskType) }}>
        <div className="input-group description-group">
          <label className="input-label">Description</label>
          <textarea
            className="tracker-textarea"
            placeholder={getDescriptionPlaceholder(selectedTaskType)}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isTracking || isCreatingTask}
            rows={2}
          />
        </div>
      </div>

      {/* Error Display */}
      {createError && (
        <div className="tracker-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{createError}</span>
          <button onClick={() => setCreateError(null)} className="error-dismiss">×</button>
        </div>
      )}

      {/* My Pending Estimates - Waiting for approval */}
      {myPendingEstimates.length > 0 && (
        <div className="my-pending-estimates-panel">
          <div className="pending-header">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span>Waiting for Approval</span>
            <span className="pending-count">{myPendingEstimates.length}</span>
          </div>
          <div className="pending-list">
            {myPendingEstimates.map(estimate => (
              <div key={estimate.id} className="pending-estimate-item">
                <div className="estimate-info">
                  <div className="estimate-task-name">{estimate.task_description}</div>
                  <div className="estimate-meta">
                    <span className="estimate-type">{estimate.task_type}</span>
                    <span className="estimate-hours">{estimate.estimate_hours}h</span>
                  </div>
                </div>
                <div className="estimate-actions">
                  <button
                    className="dismiss-estimate-btn"
                    onClick={() => handleDismissEstimate(estimate)}
                    disabled={dismissingEstimateId === estimate.id}
                    title="Dismiss this estimate (remove from list)"
                  >
                    {dismissingEstimateId === estimate.id ? (
                      <div className="btn-spinner"></div>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    )}
                  </button>
                  <button
                    className="resend-notification-btn"
                    onClick={() => handleResendNotification(estimate)}
                    disabled={resendingNotificationId === estimate.id}
                    title="Resend notification to manager"
                  >
                    {resendingNotificationId === estimate.id ? (
                      <div className="btn-spinner"></div>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 2L11 13"></path>
                          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                        Resend
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Rejected Estimates - Need revision */}
      {myRejectedEstimates.length > 0 && (
        <div className="my-rejected-estimates-panel">
          <div className="rejected-header">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span>Needs Revision</span>
            <span className="rejected-count">{myRejectedEstimates.length}</span>
          </div>
          <div className="rejected-list">
            {myRejectedEstimates.map(estimate => (
              <div key={estimate.id} className="rejected-estimate-item">
                <div className="rejected-item-header">
                  <div className="estimate-info">
                    <div className="estimate-task-name">{estimate.task_description}</div>
                    <div className="estimate-meta">
                      <span className="estimate-type">{estimate.task_type}</span>
                      <span className="estimate-hours">{estimate.estimate_hours}h</span>
                    </div>
                  </div>
                  <button
                    className="rerequest-btn"
                    onClick={() => openRerequestModal(estimate)}
                    title="Revise and re-request approval"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="23 4 23 10 17 10"></polyline>
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                    </svg>
                    Revise & Re-request
                  </button>
                </div>
                {estimate.approval_notes && (
                  <div className="rejection-reason">
                    <strong>Feedback:</strong> {estimate.approval_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved Estimates - Ready to start */}
      {approvedEstimates.length > 0 && (
        <div className="approved-estimates-panel">
          <div className="approved-header">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Approved - Ready to Start</span>
            <span className="approved-count">{approvedEstimates.length}</span>
          </div>
          <div className="approved-list">
            {approvedEstimates.map(estimate => (
              <div key={estimate.id} className="approved-estimate-item">
                <div className="estimate-info">
                  <div className="estimate-task-name">{estimate.task_description}</div>
                  <div className="estimate-meta">
                    <span className="estimate-type">{estimate.task_type}</span>
                    {estimate.project_name && <span className="estimate-project">{estimate.project_name}</span>}
                    <span className="estimate-hours">{estimate.estimate_hours}h</span>
                    {estimate.approved_by_name && (
                      <span className="approved-by">Approved by {estimate.approved_by_name}</span>
                    )}
                  </div>
                </div>
                <div className="estimate-actions">
                  <button
                    className="dismiss-estimate-btn"
                    onClick={() => handleDismissEstimate(estimate)}
                    disabled={dismissingEstimateId === estimate.id || isCreatingTask}
                    title="Remove this approved estimate"
                    aria-label="Remove this approved estimate"
                  >
                    {dismissingEstimateId === estimate.id ? (
                      <div className="btn-spinner"></div>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    )}
                  </button>
                  <button
                    className="start-approved-btn"
                    onClick={() => handleStartApprovedEstimate(estimate)}
                    disabled={isCreatingTask || dismissingEstimateId === estimate.id}
                    title="Start this task"
                  >
                    {isCreatingTask ? (
                      <div className="btn-spinner"></div>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                        Start
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Estimates for Managers */}
      {isManager && (
        <PendingEstimatesPanel
          estimates={pendingEstimates}
          loading={loadingPending}
          onApprove={handleApproveEstimate}
          onReject={handleRejectEstimate}
          onRefresh={refreshPendingEstimates}
          managerId={intranetUser?.id || ''}
          managerName={intranetUser?.name || ''}
        />
      )}

      {/* After-Hours Overrides for Managers */}
      {isManager && (
        <AfterHoursOverridePanel
          overrides={afterHoursOverrides}
          loading={afterHoursLoading}
          onAdd={addAfterHoursOverride}
          onRemove={removeAfterHoursOverride}
          onRefresh={refreshAfterHoursOverrides}
        />
      )}

      {/* Paused/Unstarted Intranet Tasks */}
      {(() => {
        // Unstarted intranet tasks (created but not running) - excludes Monday tasks shown in Sprint
        const unstartedIntranetTasks = currentTasks.filter(t => !t.isRunning);
        
        if (unstartedIntranetTasks.length === 0) return null;
        
        return (
          <div className="paused-tasks-panel">
            <div className="log-header">
              <div className="log-header-left">
                <h3>
                  <span className="paused-badge">Paused Tasks</span>
                </h3>
              </div>
              <span className="log-total">{unstartedIntranetTasks.length} paused</span>
            </div>
            <div className="entries-list">
              
              {/* Intranet tasks that aren't running */}
              {unstartedIntranetTasks.map(task => {
                const mondayMatch = task.description.match(/\[Monday #(\d+)\]/);
                const isMondayTask = !!mondayMatch;
                const mondayId = mondayMatch?.[1];
                // Check both today's tasks and upcoming tasks for Monday data
                const mondayTaskData = isMondayTask 
                  ? (mondayTasks.find(t => t.id === mondayId) || upcomingMondayTasks.find(t => t.id === mondayId)) 
                  : null;
                const cleanDescription = task.description.replace(/\[Monday #\d+\]\s*/, '');
                
                return (
                  <div 
                    key={`intranet-${task.id}`} 
                    className={`entry-item clickable ${isMondayTask ? 'monday-item' : ''}`}
                    onClick={() => handleOpenEditModal(task.id)}
                    title="Click to edit task"
                  >
                    <div className="entry-info">
                      <div className="entry-project">
                        {isMondayTask ? (
                          <>
                            <a 
                              href={mondayTaskData?.mondayUrl || `https://elink-design.monday.com/boards`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="monday-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {mondayTaskData?.boardName || getTaskProjectLabel(task)}
                              <svg className="external-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                            </a>
                            {mondayTaskData?.status && <span className="monday-status-badge">{mondayTaskData.status}</span>}
                          </>
                        ) : (
                          <span>{getTaskProjectLabel(task)}</span>
                        )}
                      </div>
                      <div className="entry-meta">
                        {isMondayTask && mondayTaskData?.type ? (
                          <span className="entry-type monday-type">{mondayTaskData.type}</span>
                        ) : (
                          <span className="entry-type">{task.type}</span>
                        )}
                        <span className="entry-task">{isMondayTask ? (mondayTaskData?.name || cleanDescription) : cleanDescription}</span>
                      </div>
                    </div>
                    {/* Show time spent vs estimate for Monday-originated tasks */}
                    {isMondayTask && mondayTaskData?.estimate ? (
                      <div className={`entry-time-vs-estimate ${getEstimateStatusClass(mondayTaskData.estimate, task.timeTotal)}`} title={`${formatDuration(task.timeTotal)} spent of ${mondayTaskData.estimate}h estimate`}>
                        <span className="time-spent">{formatDuration(task.timeTotal)}</span>
                        <span className="time-separator">/</span>
                        <span className="time-estimate">{mondayTaskData.estimate}h</span>
                      </div>
                    ) : (
                      <div className="entry-duration">
                        {formatDuration(task.timeTotal)}
                      </div>
                    )}
                    <button
                      className="entry-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditModal(task.id);
                      }}
                      title="Edit task"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="task-toggle-btn start"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleTask(task);
                      }}
                      disabled={togglingTaskId === task.id || closingTaskId === task.id}
                      title="Start task"
                    >
                      {togglingTaskId === task.id ? (
                        <div className="btn-spinner"></div>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                      )}
                    </button>
                    <button
                      className="task-close-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseTask(task);
                      }}
                      disabled={closingTaskId === task.id || togglingTaskId === task.id}
                      title="Close task"
                    >
                      {closingTaskId === task.id ? (
                        <div className="btn-spinner"></div>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Sprint View - All Monday tasks grouped by date */}
      {(() => {
        // Combine today's and upcoming tasks
        const allMondayTasks = [...mondayTasks, ...upcomingMondayTasks];
        
        // Filter out tasks that already have an intranet task (active or completed today)
        const unstartedTasks = allMondayTasks.filter(t => 
          !currentTasks.some(ct => ct.description.includes(`[Monday #${t.id}]`)) &&
          !completedTasks.some(ct => ct.description.includes(`[Monday #${t.id}]`))
        );
        
        // Group tasks by due date
        const tasksByDate: Record<string, MondayTask[]> = {};
        const today = new Date().toISOString().split('T')[0];
        
        unstartedTasks.forEach(task => {
          const date = task.dueDate || 'No Date';
          if (!tasksByDate[date]) {
            tasksByDate[date] = [];
          }
          tasksByDate[date].push(task);
        });
        
        // Sort dates
        const sortedDates = Object.keys(tasksByDate).sort((a, b) => {
          if (a === 'No Date') return 1;
          if (b === 'No Date') return -1;
          return a.localeCompare(b);
        });
        
        // Format date for display
        const formatDateLabel = (dateStr: string) => {
          if (dateStr === 'No Date') return 'No Due Date';
          if (dateStr === today) return 'Today';
          
          const date = new Date(dateStr + 'T00:00:00');
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';
          
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          
          return `${dayNames[date.getDay()]}, ${monthNames[date.getMonth()]} ${date.getDate()}`;
        };
        
        // Get date badge color
        const getDateBadgeClass = (dateStr: string) => {
          if (dateStr === 'No Date') return 'date-none';
          if (dateStr === today) return 'date-today';
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          if (dateStr === tomorrow.toISOString().split('T')[0]) return 'date-tomorrow';
          return 'date-future';
        };
        
        if (sortedDates.length === 0) return null;
        
        return (
          <div className="sprint-panel">
            <div 
              className="log-header collapsible"
              onClick={() => setShowUpcomingTasks(!showUpcomingTasks)}
            >
              <div className="log-header-left">
                <svg 
                  className={`collapse-chevron ${showUpcomingTasks ? 'expanded' : ''}`}
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
                <h3>My Sprint</h3>
                <button
                  className="sprint-refresh-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshMondayTasks();
                    refreshUpcomingTasks();
                  }}
                  disabled={mondayLoading || upcomingLoading}
                  title="Refresh tasks"
                >
                  {(mondayLoading || upcomingLoading) ? (
                    <div className="btn-spinner"></div>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 4v6h-6"></path>
                      <path d="M1 20v-6h6"></path>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                    </svg>
                  )}
                </button>
              </div>
              <span className="log-total">{unstartedTasks.length} task{unstartedTasks.length !== 1 ? 's' : ''}</span>
            </div>
            {showUpcomingTasks && (
              <div className="sprint-content">
                {sortedDates.map(dateStr => (
                  <div key={dateStr} className="sprint-date-group">
                    <div className={`sprint-date-header ${getDateBadgeClass(dateStr)}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span>{formatDateLabel(dateStr)}</span>
                      <span className="sprint-date-count">{tasksByDate[dateStr].length}</span>
                    </div>
                    <div className="entries-list monday-list">
                      {tasksByDate[dateStr].map(task => (
                        <div key={`sprint-${task.id}`} className={`entry-item monday-item ${getDateBadgeClass(dateStr)}-item`}>
                          <div className="entry-info">
                            <div className="entry-project">
                              <a 
                                href={task.mondayUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="monday-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {task.boardName}
                                <svg className="external-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                  <polyline points="15 3 21 3 21 9"></polyline>
                                  <line x1="10" y1="14" x2="21" y2="3"></line>
                                </svg>
                              </a>
                              {task.status && <span className="monday-status-badge">{task.status}</span>}
                            </div>
                            <div className="entry-meta">
                              {task.type && <span className="entry-type monday-type">{task.type}</span>}
                              <span className="entry-task">{task.name}</span>
                            </div>
                          </div>
                          {(() => {
                            const timeSpent = getTimeSpentOnMondayTask(task.id);
                            if (task.estimate || timeSpent > 0) {
                              return (
                                <div 
                                  className={`entry-time-vs-estimate ${task.estimate ? getEstimateStatusClass(task.estimate, timeSpent) : ''}`}
                                  title={timeSpent > 0 
                                    ? `${formatDuration(timeSpent)} spent${task.estimate ? ` of ${task.estimate}h estimate` : ''}`
                                    : task.estimate ? `Estimate: ${task.estimate}h` : ''
                                  }
                                >
                                  {timeSpent > 0 && (
                                    <>
                                      <span className="time-spent">{formatDuration(timeSpent)}</span>
                                      {task.estimate && <span className="time-separator">/</span>}
                                    </>
                                  )}
                                  {task.estimate && <span className="time-estimate">{task.estimate}h</span>}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          <button
                            className="task-toggle-btn start monday-start-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartMondayTask(task);
                            }}
                            disabled={startingMondayTaskId === task.id || loadingProjects}
                            title="Start this task"
                          >
                            {startingMondayTaskId === task.id || loadingProjects ? (
                              <div className="btn-spinner"></div>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                              </svg>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Active Tasks - Only running tasks */}
      {(() => {
        const runningTasks = currentTasks.filter(t => t.isRunning);
        if (runningTasks.length === 0) return null;
        
        return (
        <div className="current-tasks-panel">
          <div 
            className="log-header collapsible"
            onClick={() => setShowActiveTasks(!showActiveTasks)}
          >
            <div className="log-header-left">
              <svg 
                className={`collapse-chevron ${showActiveTasks ? 'expanded' : ''}`}
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
              <h3>Active Tasks</h3>
            </div>
              <span className="log-total">{runningTasks.length} task{runningTasks.length !== 1 ? 's' : ''}</span>
          </div>
          {showActiveTasks && (
            <div className="entries-list">
                {runningTasks.map(task => {
                  const mondayMatch = task.description.match(/\[Monday #(\d+)\]/);
                  const isMondayTask = !!mondayMatch;
                  const mondayId = mondayMatch?.[1];
                  // Check both today's tasks and upcoming tasks for Monday data
                  const mondayTaskData = isMondayTask 
                    ? (mondayTasks.find(t => t.id === mondayId) || upcomingMondayTasks.find(t => t.id === mondayId)) 
                    : null;
                  const cleanDescription = task.description.replace(/\[Monday #\d+\]\s*/, '');
                  
                  return (
                    <div 
                      key={task.id} 
                      className={`entry-item running clickable ${isMondayTask ? 'monday-item' : ''}`}
                      onClick={() => handleOpenEditModal(task.id)}
                      title="Click to edit task"
                    >
                  <div className="entry-info">
                    <div className="entry-project">
                          {isMondayTask ? (
                            <>
                              <a 
                                href={mondayTaskData?.mondayUrl || `https://elink-design.monday.com/boards`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="monday-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {mondayTaskData?.boardName || getTaskProjectLabel(task)}
                                <svg className="external-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                  <polyline points="15 3 21 3 21 9"></polyline>
                                  <line x1="10" y1="14" x2="21" y2="3"></line>
                                </svg>
                              </a>
                              {mondayTaskData?.status && <span className="monday-status-badge">{mondayTaskData.status}</span>}
                            </>
                          ) : (
                            <span>{getTaskProjectLabel(task)}</span>
                          )}
                          <span className="running-badge">RUNNING</span>
                    </div>
                    <div className="entry-meta">
                          {isMondayTask && mondayTaskData?.type ? (
                            <span className="entry-type monday-type">{mondayTaskData.type}</span>
                          ) : (
                      <span className="entry-type">{task.type}</span>
                          )}
                          <span className="entry-task">{isMondayTask ? (mondayTaskData?.name || cleanDescription) : cleanDescription}</span>
                    </div>
                  </div>
                  {/* Show time spent vs estimate for Monday-originated tasks */}
                  {isMondayTask && mondayTaskData?.estimate ? (
                    <div className={`entry-time-vs-estimate running ${getEstimateStatusClass(mondayTaskData.estimate, task.timeTotal + getRunningTaskElapsed(task))}`} title={`${formatTime(task.timeTotal + getRunningTaskElapsed(task))} spent of ${mondayTaskData.estimate}h estimate`}>
                      <span className="time-spent">{formatTime(task.timeTotal + getRunningTaskElapsed(task))}</span>
                      <span className="time-separator">/</span>
                      <span className="time-estimate">{mondayTaskData.estimate}h</span>
                    </div>
                  ) : (
                    <div className="entry-duration">
                      {formatTime(task.timeTotal + getRunningTaskElapsed(task))}
                    </div>
                  )}
                  <button
                    className="entry-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEditModal(task.id);
                    }}
                    title="Edit task"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button
                        className="task-toggle-btn stop"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleTask(task);
                    }}
                    disabled={togglingTaskId === task.id || closingTaskId === task.id}
                        title="Stop task"
                  >
                    {togglingTaskId === task.id ? (
                      <div className="btn-spinner"></div>
                        ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="6" width="12" height="12" rx="2"></rect>
                      </svg>
                    )}
                  </button>
                  <button
                    className="task-close-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseTask(task);
                    }}
                    disabled={closingTaskId === task.id || togglingTaskId === task.id}
                    title="Close task"
                  >
                    {closingTaskId === task.id ? (
                      <div className="btn-spinner"></div>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </button>
                </div>
                  );
                })}
            </div>
          )}
        </div>
        );
      })()}

      {/* Today's Log (Collapsible - always visible) */}
      {showLog && (
        <div className="today-log-panel">
          <div 
            className="log-header collapsible"
            onClick={() => setShowTodaysLog(!showTodaysLog)}
          >
            <div className="log-header-left">
              <svg 
                className={`collapse-chevron ${showTodaysLog ? 'expanded' : ''}`}
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            <h3>Today's Log</h3>
            </div>
            <span className="log-total">{formatDuration(getTotalTimeToday())} total</span>
          </div>
          {showTodaysLog && (
            <div className="entries-list">
              {isTracking && (
                <div className="entry-item current">
                  <div className="entry-info">
                    <div className="entry-project">
                      {showProjectDropdown ? getSelectedProjectName() : getSelectedAccountName()}
                    </div>
                    <div className="entry-meta">
                      <span className="entry-type">{selectedTaskType}</span>
                      <span className="entry-task">{description || 'No description'}</span>
                    </div>
                  </div>
                  <div className="entry-duration">{formatDuration(elapsedTime)}</div>
                </div>
              )}
              {/* Completed Tasks from Intranet */}
              {completedTasks.map(task => {
                const mondayMatch = task.description.match(/\[Monday #(\d+)\]/);
                const isMondayTask = !!mondayMatch;
                const mondayId = mondayMatch?.[1];
                const mondayTaskData = isMondayTask 
                  ? (mondayTasks.find(t => t.id === mondayId) || upcomingMondayTasks.find(t => t.id === mondayId)) 
                  : null;
                const cleanDescription = task.description.replace(/\[Monday #\d+\]\s*/, '');
                
                return (
                  <div 
                    key={task.id} 
                    className={`entry-item completed clickable ${isMondayTask ? 'monday-item' : ''}`}
                    onClick={() => handleOpenEditModal(task.id)}
                    title="Click to edit task"
                  >
                    <div className="entry-info">
                      <div className="entry-project">
                        {isMondayTask ? (
                          <>
                            <a 
                              href={mondayTaskData?.mondayUrl || `https://elink-design.monday.com/boards`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="monday-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {mondayTaskData?.boardName || getTaskProjectLabel(task)}
                              <svg className="external-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                            </a>
                          </>
                        ) : (
                          <span>{getTaskProjectLabel(task)}</span>
                        )}
                        <span className="completed-badge">DONE</span>
                      </div>
                      <div className="entry-meta">
                        <span className="entry-type">{task.type}</span>
                        <span className="entry-task">{isMondayTask ? (mondayTaskData?.name || cleanDescription) : cleanDescription}</span>
                      </div>
                    </div>
                    <div className="entry-duration">
                      {formatDuration(task.timeTotal)}
                    </div>
                    <button 
                      className="entry-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditModal(task.id);
                      }}
                      title="Edit task"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {/* Local entries */}
              {todayEntries.map(entry => (
                <div key={entry.id} className="entry-item">
                  <div className="entry-info">
                    <div className="entry-project">
                      {cleanTaskLabel(entry.projectName) || cleanTaskLabel(entry.accountName)}
                    </div>
                    <div className="entry-meta">
                      <span className="entry-type">{entry.taskType}</span>
                      <span className="entry-task">{entry.description || 'No description'}</span>
                    </div>
                  </div>
                  <div className="entry-duration">{formatDuration(entry.duration)}</div>
                  <button 
                    className="entry-delete"
                    onClick={() => handleDeleteEntry(entry.id)}
                    title="Delete entry"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeTracker;
